import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureColumnComparisonChart } from '../../ChartConfiguration/ChartConfiguration';
import { getBhDiagnosticSummary } from '../HedisMetricService';
import BhDiagnosticSummary from '../../../Models/HedisMetrics/BhDiagnosticSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const BhDiagnosticSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [bhDiagnosticSummary, setBhDiagnosticSummary] = useState<BhDiagnosticSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        bhDiagnosticHealthPlans,
        setBhDiagnosticHealthPlans,
        bhDiagnosticTeam,
        setBhDiagnosticTeam,
        bhDiagnosticDateFilter,
        setBhDiagnosticDateFilter,
        bhDiagnosticFromDate,
        setBhDiagnosticFromDate,
        bhDiagnosticToDate,
        setBhDiagnosticToDate,
        bhDiagnosticIncludePastOpenStatus,
        setBhDiagnosticIncludePastOpenStatus,
        bhDiagnosticShowInActiveTeam,
        setBhDiagnosticShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureColumnComparisonChart('BH Diagnostic Summary', 'Completed vs Not Completed', 'Type', 'Count', [{ name: '', data: [0] }], [0], null, chartHeight)
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (bhDiagnosticDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(bhDiagnosticFromDate) && Boolean(bhDiagnosticToDate))) {
            const changeFilter = async () => {
                let start = bhDiagnosticFromDate !== null ? new Date(bhDiagnosticFromDate).toDateString() : null;
                let end = bhDiagnosticToDate !== null ? new Date(bhDiagnosticToDate).toDateString() : null;
                const data = await getBhDiagnosticSummary(
                    bhDiagnosticHealthPlans,
                    bhDiagnosticTeam,
                    bhDiagnosticDateFilter,
                    start,
                    end,
                    bhDiagnosticIncludePastOpenStatus,
                    bhDiagnosticShowInActiveTeam
                );
                setBhDiagnosticSummary(data);
                setIsPrepared(true);
            };
            if ((bhDiagnosticFromDate !== null || bhDiagnosticToDate !== null) && bhDiagnosticToDate < bhDiagnosticFromDate) {
            } else {
                setChartOptions(
                    configureColumnComparisonChart(
                        'BH Diagnostic Summary',
                        'Completed vs Not Completed',
                        'Type',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        bhDiagnosticHealthPlans,
        bhDiagnosticTeam,
        bhDiagnosticDateFilter,
        bhDiagnosticFromDate,
        bhDiagnosticToDate,
        bhDiagnosticIncludePastOpenStatus,
        bhDiagnosticShowInActiveTeam,
    ]);

    useEffect(() => {
        if (bhDiagnosticSummary && isPrepared) {
            let xAxisValues = [];
            let yAxisValues = [];
            let completedValues = [];
            let notCompletedValues = [];
            for (var data in bhDiagnosticSummary.BHDiagnostics) {
                let dataInfo = bhDiagnosticSummary.BHDiagnostics[data];

                let d = {
                    y: dataInfo.CompletedCount,
                };
                completedValues.push(d);

                let d1 = {
                    y: dataInfo.NotCompletedCount,
                    name: '',
                };
                notCompletedValues.push(d1);

                xAxisValues.push(dataInfo.Type);
            }
            yAxisValues.push({ name: 'Completed', data: completedValues, color: '#00C853', showInLegend: true });
            yAxisValues.push({ name: 'Not Completed', data: notCompletedValues, color: '#BDBDBD', showInLegend: true });
            if (bhDiagnosticSummary.BHDiagnostics.length === 0) {
                setChartOptions(configureColumnComparisonChart('BH Diagnostic Summary', 'Members', 'Type', 'Count', [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                    [0], null, chartHeight));
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setChartOptions(configureColumnComparisonChart('BH Diagnostic Summary', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bhDiagnosticSummary, bhDiagnosticHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(bhDiagnosticHealthPlans);
        setTeam(bhDiagnosticTeam);
        setFilter(bhDiagnosticDateFilter);
        setFDate(bhDiagnosticFromDate);
        setTDate(bhDiagnosticToDate);
        setIncludePastOpenStatus(bhDiagnosticIncludePastOpenStatus);
        setShowInActiveTeam(bhDiagnosticShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.BHDiagnostic);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/BHDiagnostic`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            BH Diagnostic
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">This widget shows you the members who have completed their BH diagnostic assessment in the last 6 months and also the members who have not completed their BH diagnostic assessment in the last 6 months.</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        All :
                                    </Typography>
                                    <Typography variant="caption">Total members who have a BH diagnostic assessment performed</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Primary BH :
                                    </Typography>
                                    <Typography variant="caption">Members with BH primary who have a BH diagnostic assessment performed</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        SMI :
                                    </Typography>
                                    <Typography variant="caption">Members who are SMI or RHBA and have a BH diagnostic assessment performed</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={bhDiagnosticTeam}
                        setSelectedTeam={setBhDiagnosticTeam}
                        dateFilter={bhDiagnosticDateFilter}
                        setDateFilter={setBhDiagnosticDateFilter}
                        fromDate={bhDiagnosticFromDate}
                        setFromDate={setBhDiagnosticFromDate}
                        toDate={bhDiagnosticToDate}
                        setToDate={setBhDiagnosticToDate}
                        healthPlan={bhDiagnosticHealthPlans}
                        setHealthPlan={setBhDiagnosticHealthPlans}
                        includePastOpenStatusMembers={bhDiagnosticIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setBhDiagnosticIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.BHDiagnostic}
                        showInActiveTeams={bhDiagnosticShowInActiveTeam}
                        setShowInActiveTeams={setBhDiagnosticShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default BhDiagnosticSummaries;
